import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useSystem } from '../../../../hooks/useSystem';
import { JourneyNavigationHandler, Routes } from '../../../../routes';
import { styled } from '../../../../theme';
import { BreadCrumbWidget } from '../../../../widgets/header/BreadCrumbWidget';
import { CenterFormOrientation, CenterFormWidget } from '../../../../widgets/layout/CenterFormWidget';
import { BillingDetailsStepWidget } from './BillingDetailsStepWidget';
import { FinalConfirmationStep } from './FinalConfirmationStep';
import { SelectCardDetailsWidget } from './SelectCardDetailsStepWidget';
import { ENTERPRISE_ID, SelectPlanStepWidget } from './SelectPlanStepWidget';
import { EnterprisePlanStepWidget } from './EnterprisePlanStepWidget';

export interface UpgradePlanDialogProps {
  handler: JourneyNavigationHandler<Routes.UPGRADE_PLAN>;
}

enum UpgradeStep {
  UPGRADE = 'Select upgrade',
  BILLING = 'Billing details',
  CARD = 'Card details',
  REVIEW = 'Review order',
  ENTERPRISE = 'Upgrade Info'
}

const ORDER = [UpgradeStep.UPGRADE, UpgradeStep.BILLING, UpgradeStep.CARD, UpgradeStep.REVIEW];

export const UpgradePlanDialog: React.FC<UpgradePlanDialogProps> = observer((props) => {
  const { handler } = props;
  const system = useSystem();
  const org = system.orgStore.current_org;
  const plan = org?.plans.items?.find((p) => p.model.id === handler.params.plan_id);

  const [upgrade, setUpgrade] = useState<string>();
  const [card, setCard] = useState<string>();
  const [step, setStep] = useState(ORDER[0]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!upgrade && plan?.upgrades?.[0]) {
      setUpgrade(plan?.upgrades?.[0].id);
    }
  }, [upgrade, plan?.upgrades]);

  const selectedUpgrade = plan?.upgrades.find((u) => u.id === upgrade);

  return (
    <CenterFormWidget
      noLogo={true}
      loading={loading}
      primaryLabel="Upgrade subscription"
      secondaryLabel={plan ? `Upgrading from '${plan?.model?.label}'` : ''}
      orientation={CenterFormOrientation.ROW}
    >
      {step === UpgradeStep.ENTERPRISE ? null : (
        <S.Container>
          <BreadCrumbWidget steps={ORDER} currentStep={ORDER.indexOf(step)} />
        </S.Container>
      )}
      {step === UpgradeStep.UPGRADE ? (
        <SelectPlanStepWidget
          onCancel={() => {
            handler.navigate(Routes.MANAGE_PLANS, {
              ...handler.params
            });
          }}
          selected={upgrade}
          onSelected={(u) => {
            setUpgrade(u);
          }}
          onNext={() => {
            if (upgrade === ENTERPRISE_ID) {
              setStep(UpgradeStep.ENTERPRISE);
            } else {
              setStep(ORDER[ORDER.indexOf(step) + 1]);
            }
          }}
          upgrades={plan?.upgrades || []}
        />
      ) : null}
      {step === UpgradeStep.BILLING ? (
        <BillingDetailsStepWidget
          onCancel={() => {
            setStep(ORDER[ORDER.indexOf(step) - 1]);
          }}
          onNext={() => {
            setStep(ORDER[ORDER.indexOf(step) + 1]);
          }}
        />
      ) : null}
      {step === UpgradeStep.CARD ? (
        <SelectCardDetailsWidget
          legalUrl={selectedUpgrade?.external_link}
          onCancel={() => {
            setStep(ORDER[ORDER.indexOf(step) - 1]);
          }}
          org_id={org?.id!}
          card={card}
          cardSelected={(card) => {
            setCard(card);
          }}
          onNext={() => {
            // we load the upgrades again here because we can now get tax information based on the billing details
            setLoading(true);
            plan?.loadUpgrades().then((u) => {
              setLoading(false);
              setStep(ORDER[ORDER.indexOf(step) + 1]);
            });
          }}
        />
      ) : null}
      {step === UpgradeStep.REVIEW ? (
        <FinalConfirmationStep
          back={() => {
            setStep(ORDER[ORDER.indexOf(step) - 1]);
          }}
          card_digits={org?.paymentConfigurations.items?.find((c) => c.model.id === card)?.model.last_4 || ''}
          proccess={async () => {
            setLoading(true);
            await system.clientProvisioner.provisionChargingStationClient().upgrade({
              blueprint_id: upgrade!,
              plan_id: plan!.model.id!,
              payment_configuration: card!
            });
            handler.navigate(Routes.MANAGE_PLANS, {
              ...handler.params
            });
            org?.loadPlans();
            system.dialogStore.infoDialog({
              title: 'Success',
              message: 'Your upgrade has been processed'
            });
          }}
          upgrade={selectedUpgrade!}
        />
      ) : null}
      {step === UpgradeStep.ENTERPRISE ? (
        <EnterprisePlanStepWidget
          close={() => {
            handler.navigate(Routes.MANAGE_PLANS, {
              ...handler.params
            });
          }}
        />
      ) : null}
    </CenterFormWidget>
  );
});

namespace S {
  export const Container = styled.div`
    display: flex;
    align-items: center;
    flex-direction: column;
  `;
}
